import React, { useEffect, useRef, useState } from 'react';
import { ResumeData } from '../../../../../types/resume';
import { templates } from '../../../templates';
import ActionButtons from './ActionButtons';

interface Props {
  data: ResumeData;
  onUpdate: (data: Partial<ResumeData>) => void;
}

export default function FinalizeForm({ data }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const selectedTemplate = templates.find(t => t.id === data.template);
  const TemplateComponent = selectedTemplate?.component;

  // Calculate scale based on container width
  useEffect(() => {
    const calculateScale = () => {
      if (!previewRef.current) return;
      
      // A4 dimensions in pixels (assuming 96 DPI)
      const A4_WIDTH = 794; // 210mm
      const containerWidth = previewRef.current.offsetWidth;
      const newScale = Math.min((containerWidth - 48) / A4_WIDTH, 1);
      setScale(newScale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  if (!TemplateComponent) {
    return (
      <div className="text-center text-gray-600 p-8">
        <p>Please select a template first</p>
        <button
          onClick={() => window.history.back()}
          className="mt-4 px-4 py-2 text-sm text-teal-600 hover:text-teal-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900">Review & Download</h2>
        <p className="mt-2 text-gray-600">
          Preview your resume and download it in PDF format. Make sure everything looks perfect before downloading.
        </p>
      </div>

      <div ref={previewRef} className="bg-gray-100 rounded-xl p-4 sm:p-6">
        <div 
          id="resume-preview" 
          className="bg-white shadow-xl mx-auto origin-top overflow-hidden"
          style={{ 
            width: '21cm',
            minHeight: '29.7cm',
            maxWidth: '100%',
            transform: `scale(${scale})`,
            marginBottom: `${(1 - scale) * 100}%`
          }}
        >
          <TemplateComponent data={data} />
        </div>
      </div>

      <div className="max-w-md mx-auto">
        <ActionButtons data={data} />
        <p className="text-sm text-gray-500 text-center mt-4">
          Your resume will be downloaded as a PDF file that matches exactly what you see here.
          The PDF will be properly formatted for printing and sharing.
        </p>
      </div>
    </div>
  );
}